// useSSE.js — Shared singleton SSE connection
// All components share ONE connection. Events distributed via subscriptions.
import { useEffect, useRef } from 'react';

const API_BASE = process.env.REACT_APP_API_URL || '';

// ── Singleton SSE manager ─────────────────────────────────────────────────────
class SSEManager {
  constructor() {
    this.es = null;
    this.subscribers = new Map(); // id -> { projectId, handlers }
    this.nextId = 0;
    this.reconnectTimer = null;
    this.active = false;
    this.failCount = 0;
  }

  start() {
    if (this.active) return;
    this.active = true;
    this.connect();
  }

  stop() {
    this.active = false;
    if (this.es) { this.es.close(); this.es = null; }
    if (this.reconnectTimer) { clearTimeout(this.reconnectTimer); this.reconnectTimer = null; }
  }

  connect() {
    if (!this.active) return;
    try {
      // Send token as query param — EventSource cannot send Authorization headers
      // This ensures SSE works on HTTP where cookies may be unreliable
      const token = localStorage.getItem('flowup_token') || '';
      const url = `${API_BASE}/api/events${token ? '?token='+encodeURIComponent(token) : ''}`;
      this.es = new EventSource(url, { withCredentials: true });

      this.es.onopen = () => { this.failCount = 0; };

      this.es.onmessage = (e) => {
        try {
          const data = JSON.parse(e.data);
          if (data.type === 'ping' || data.type === 'connected') return;
          const isGlobal = data.projectId === '__global__';
          // Deliver to all subscribers
          this.subscribers.forEach(({ projectId, handlers }) => {
            // Wildcard subscribers get all events (AppShell uses __appshell__)
            const isWildcard = projectId === '__appshell__';
            // Only deliver if event matches this subscriber's project, is global, or subscriber is wildcard
            if (!isWildcard && !isGlobal && data.projectId && data.projectId !== projectId) return;
            const handler = handlers.current[data.type];
            if (handler) {
              try { handler(data); } catch(_) {}
            }
          });
        } catch(_) {}
      };

      this.es.onerror = () => {
        if (this.es) { this.es.close(); this.es = null; }
        this.failCount++;
        // Exponential backoff: 1s, 2s, 4s, 8s, max 15s
        const delay = Math.min(15000, 1000 * Math.pow(2, Math.min(this.failCount - 1, 4)));
        if (this.active) {
          this.reconnectTimer = setTimeout(() => this.connect(), delay);
        }
      };
    } catch(e) {}
  }

  subscribe(projectId, handlers) {
    const id = ++this.nextId;
    this.subscribers.set(id, { projectId, handlers });
    // Start connection if first subscriber
    if (this.subscribers.size === 1) this.start();
    return id;
  }

  unsubscribe(id) {
    this.subscribers.delete(id);
    // Stop connection if no subscribers
    if (this.subscribers.size === 0) this.stop();
  }
}

const manager = new SSEManager();

// ── React hook — uses shared connection ───────────────────────────────────────
export default function useSSE(projectId, handlers) {
  const handlersRef = useRef(handlers);
  handlersRef.current = handlers; // always latest without re-subscribing

  useEffect(() => {
    if (!projectId) return;
    const subId = manager.subscribe(projectId, handlersRef);
    return () => manager.unsubscribe(subId);
  }, [projectId]);
}
